import React, { useState, useEffect } from 'react';
import api from '../services/api';
import RoleList from '../components/admin/RoleList';
import PermissionList from '../components/admin/PermissionList';
import SyncPermissionsButton from '../components/admin/SyncPermissionsButton';
import RoleFormModal from '../components/admin/RoleFormModal';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
// eslint-disable-next-line no-unused-vars
import { FiEdit } from 'react-icons/fi';
import ConditionalButton from '../components/common/ConditionalButton';

const RolesPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('');
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();
  // console.log('user.permissions:', user?.permissions);

  // Charger les rôles et permissions au montage
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [rolesRes, permsRes] = await Promise.all([
          api.get('/roles'),
          api.get('/permissions')
        ]);
        setRoles(rolesRes.data.data || []);
        setPermissions(permsRes.data.data || []);
        if (rolesRes.data.data && rolesRes.data.data.length > 0) {
          setSelectedRole(rolesRes.data.data[0]);
        }
      } catch (e) {
        setRoles([]);
        setPermissions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Charger les permissions du rôle sélectionné
  useEffect(() => {
    const fetchRolePermissions = async () => {
      if (!selectedRole) return;
      try {
        const res = await api.get(`/roles/${selectedRole.id}/permissions`);
        setRolePermissions(res.data.data || []);
      } catch (e) {
        setRolePermissions([]);
      }
    };
    fetchRolePermissions();
  }, [selectedRole]);

  const handleSelectRole = (role) => {
    setSelectedRole(role);
  };

  const handleTogglePermission = async (permId) => {
    let newPerms;
    if (rolePermissions.includes(permId)) {
      newPerms = rolePermissions.filter((id) => id !== permId);
    } else {
      newPerms = [...rolePermissions, permId];
    }
    setRolePermissions(newPerms);
    // Sauvegarde immédiate
    if (selectedRole) {
      try {
        await api.put(`/roles/${selectedRole.id}/permissions`, {
          permissions: newPerms
        });
        toast.success('Permissions enregistrées');
      } catch (e) {
        toast.error('Erreur lors de la sauvegarde');
      }
    }
  };

  const handleSync = async () => {
    const result = await Swal.fire({
      title: 'Synchroniser les permissions',
      text: 'Cette action va régénérer toutes les permissions du système à partir du code source. Cela peut ajouter de nouvelles permissions ou corriger des incohérences. Voulez-vous continuer ?',
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#aaa',
      confirmButtonText: 'Oui, synchroniser',
      cancelButtonText: 'Annuler',
    });
    if (!result.isConfirmed) return;
    
    setLoading(true);
    try {
      await api.post('/permissions/sync');
      
      // Recharger toutes les données après synchronisation
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/roles'),
        api.get('/permissions')
      ]);
      
      setRoles(rolesRes.data.data || []);
      setPermissions(permsRes.data.data || []);
      
      // Recharger les permissions du rôle sélectionné si un rôle est sélectionné
      if (selectedRole) {
        try {
          const rolePermsRes = await api.get(`/roles/${selectedRole.id}/permissions`);
          setRolePermissions(rolePermsRes.data.data || []);
        } catch (e) {
          console.error('Erreur lors du rechargement des permissions du rôle:', e);
          setRolePermissions([]);
        }
      }
      
      await Swal.fire('Synchronisation terminée', 'Les permissions ont été synchronisées avec succès.', 'success');
    } catch (e) {
      console.error('Erreur lors de la synchronisation:', e);
      await Swal.fire('Erreur', 'Erreur lors de la synchronisation des permissions.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = () => {
    setEditingRole(null);
    setShowRoleModal(true);
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setShowRoleModal(true);
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div className="flex gap-8">
      <div className="w-64">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Rôles</h2>
          <ConditionalButton
            entity="roles"
            permission="create"
            className="bg-primary-600 text-white px-2 py-1 rounded"
            onClick={handleAddRole}
          >
            +
          </ConditionalButton>
        </div>
        <RoleList roles={roles} selectedRole={selectedRole} onSelect={handleSelectRole} onEdit={handleEditRole} />
      </div>
      <div className="flex-1">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Permissions du rôle</h2>
          <SyncPermissionsButton onSync={handleSync} loading={loading} />
        </div>
        {/* Filtre par entité */}
        <div className="mb-4">
          <select
            className="border rounded px-3 py-2"
            value={entityFilter}
            onChange={e => setEntityFilter(e.target.value)}
          >
            <option value="">Toutes les entités</option>
            {[...new Set(permissions.map(p => p.name.split('.')[0]))].sort().map(entity => (
              <option key={entity} value={entity}>{entity}</option>
            ))}
          </select>
        </div>
        <PermissionList
          permissions={permissions.filter(p => !entityFilter || p.name.startsWith(entityFilter + '.'))}
          rolePermissions={rolePermissions}
          onToggle={handleTogglePermission}
        />
      </div>
      {showRoleModal && (
        <RoleFormModal
          role={editingRole}
          onClose={() => setShowRoleModal(false)}
          onSuccess={() => setShowRoleModal(false)}
        />
      )}
    </div>
  );
};

export default RolesPermissions;
